<template>
  <div class="grid gap-4 md:grid-cols-2">
    <div class="space-y-2">
      <label for="college" class="block text-sm font-medium text-gray-700">
        College/Institute
      </label>
      <select
        id="college"
        v-model="selectedCollege"
        @change="onCollegeChange"
        class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
      >
        <option value="">Select College/Institute</option>
        <option v-for="college in colleges" :key="college.id" :value="college.name_en">
          {{ college.name_en }}
        </option>
      </select>
    </div>

    <div class="space-y-2">
      <label for="department" class="block text-sm font-medium text-gray-700">
        Department
      </label>
      <select
        id="department"
        v-model="selectedDepartment"
        @change="onDepartmentChange"
        :disabled="!selectedCollege"
        class="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 transition focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500"
      >
        <option value="">
          {{ selectedCollege ? 'Select Department' : 'Select college first' }}
        </option>
        <option
          v-for="department in availableDepartments"
          :key="department.id"
          :value="department.name_en"
        >
          {{ department.name_en }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';

interface DepartmentOption {
  id: number;
  name_en: string;
  name_ar?: string | null;
  name_ku?: string | null;
}

interface CollegeOption {
  id: number;
  name_en: string;
  name_ar?: string | null;
  name_ku?: string | null;
  departments: DepartmentOption[];
}

interface Props {
  college?: string;
  department?: string;
}

interface Emits {
  (e: 'update:college', value: string): void;
  (e: 'update:department', value: string): void;
}

declare global {
  interface Window {
    submitPortalBoot?: {
      colleges?: CollegeOption[];
    };
  }
}

const props = withDefaults(defineProps<Props>(), {
  college: '',
  department: '',
});

const emit = defineEmits<Emits>();

const selectedCollege = ref(props.college);
const selectedDepartment = ref(props.department);

const colleges = computed<CollegeOption[]>(() => {
  const configured = window.submitPortalBoot?.colleges ?? [];

  if (!selectedCollege.value || configured.some((college) => college.name_en === selectedCollege.value)) {
    return configured;
  }

  return [
    ...configured,
    {
      id: -1,
      name_en: selectedCollege.value,
      departments: selectedDepartment.value
        ? [{ id: -1, name_en: selectedDepartment.value }]
        : [],
    },
  ];
});

const availableDepartments = computed<DepartmentOption[]>(() => {
  if (!selectedCollege.value) {
    return [];
  }

  const departmentOptions =
    colleges.value.find((college) => college.name_en === selectedCollege.value)?.departments ?? [];

  if (!selectedDepartment.value || departmentOptions.some((department) => department.name_en === selectedDepartment.value)) {
    return departmentOptions;
  }

  return [
    ...departmentOptions,
    {
      id: -1,
      name_en: selectedDepartment.value,
    },
  ];
});

const onCollegeChange = () => {
  selectedDepartment.value = '';
  emit('update:college', selectedCollege.value);
  emit('update:department', '');
};

const onDepartmentChange = () => {
  emit('update:department', selectedDepartment.value);
};

watch(
  () => props.college,
  (value) => {
    selectedCollege.value = value;
  },
);

watch(
  () => props.department,
  (value) => {
    selectedDepartment.value = value;
  },
);
</script>
